<template>
  <div class="jadwal-page">
    <!-- Sidebar / Navigation padding -->
    <v-container fluid class="pa-5">
      <!-- Title Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <h1 class="text-h4 font-weight-bold white--text">
            Jadwal Petugas PST
          </h1>
          <p class="text-overline cyan--text text--lighten-3">
            Kelola Jadwal Jaga PST
          </p>
        </v-col>
      </v-row>
      <v-row>
        <v-btn
          small
          depressed
          color="grey darken-3"
          class="mr-2 white--text"
          @click="openImport('jadwal')"
        >
          <v-icon left small>mdi-file-import</v-icon> Import Jadwal Petugas
        </v-btn>
        <v-btn
          small
          depressed
          color="grey darken-3"
          class="mr-2 white--text"
          @click="openImport('libur')"
        >
          <v-icon left small>mdi-calendar-remove</v-icon> Import Hari Libur
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn
          small
          depressed
          color="cyan darken-3"
          class="white--text"
          to="/dashboard"
          target="_blank"
        >
          <v-icon left small>mdi-view-dashboard</v-icon> Lihat Dashboard
        </v-btn>
      </v-row>

      <v-row>
        <!-- ══ LEFT COLUMN: CALENDAR ══ -->
        <v-col cols="12" lg="8">
          <v-card
            hover
            color="grey darken-4"
            class="calendar-card rounded-xl overflow-hidden"
          >
            <!-- Calendar Header -->
            <div class="calendar-header pa-6">
              <div class="month-nav d-flex align-center">
                <h2 class="month-title white--text mb-0">
                  {{ formatMonthYear(currentDate) }}
                </h2>
                <div class="nav-btns ml-4">
                  <v-btn
                    icon
                    small
                    dark
                    color="grey lighten-1"
                    @click="prevMonth"
                    ><v-icon>mdi-chevron-left</v-icon></v-btn
                  >
                  <v-btn
                    icon
                    small
                    color="cyan lighten-3"
                    class="mr-2"
                    @click="setToday"
                  >
                    <!-- ══ <v-icon>mdi-calendar-today</v-icon> ══ -->
                    Today
                  </v-btn>
                  <v-btn
                    icon
                    small
                    dark
                    color="grey lighten-1"
                    @click="nextMonth"
                    ><v-icon>mdi-chevron-right</v-icon></v-btn
                  >
                </div>
              </div>
              <div class="view-modes d-flex align-center">
                <v-btn-toggle
                  v-model="viewMode"
                  mandatory
                  dark
                  dense
                  class="mode-toggle"
                  color="cyan lighten-3"
                >
                  <v-btn x-small value="month" class="px-4">MONTH</v-btn>
                  <v-btn x-small value="week" class="px-4">WEEK</v-btn>
                  <v-btn x-small value="list" class="px-4">LIST</v-btn>
                </v-btn-toggle>
              </div>
            </div>

            <!-- Calendar Body -->
            <div class="calendar-body px-6 pb-6">
              <!-- ── MONTH VIEW ── -->
              <template v-if="viewMode === 'month'">
                <div class="week-days">
                  <div
                    class="week-day-label"
                    v-for="day in [
                      'SEN',
                      'SEL',
                      'RAB',
                      'KAM',
                      'JUM',
                      'SAB',
                      'MIN',
                    ]"
                    :key="day"
                  >
                    {{ day }}
                  </div>
                </div>
                <div class="dates-grid">
                  <div
                    v-for="(dateObj, idx) in calendarDates"
                    :key="idx"
                    class="date-cell"
                    :class="{
                      'other-month': !dateObj.isCurrentMonth,
                      'is-today': dateObj.isToday,
                    }"
                    @click="openDialog(dateObj.dateStr)"
                  >
                    <span class="date-num">{{ dateObj.day }}</span>
                    <div class="cell-content">
                      <div
                        v-if="isHoliday(dateObj.dateStr)"
                        class="holiday-badge"
                      >
                        LIBUR
                      </div>
                      <div
                        v-for="sched in getSchedulesForDate(dateObj.dateStr)"
                        :key="sched.id"
                        class="schedule-badge"
                        :class="sched.shift_type.toLowerCase()"
                        @click.stop="editSchedule(sched)"
                      >
                        <span class="badge-text">{{
                          sched.user
                            ? formatShortName(sched.user.name)
                            : "Petugas"
                        }}</span>
                        <v-icon
                          x-small
                          class="badge-dot"
                          :color="
                            sched.shift_type === 'Pagi'
                              ? 'cyan'
                              : sched.shift_type === 'Siang'
                              ? 'orange'
                              : 'green accent-4'
                          "
                          >mdi-circle</v-icon
                        >
                      </div>
                      <div
                        v-if="
                          getSchedulesForDate(dateObj.dateStr).length === 0 &&
                          !isHoliday(dateObj.dateStr)
                        "
                        class="no-shift"
                      >
                        -
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- ── WEEK VIEW ── -->
              <template v-else-if="viewMode === 'week'">
                <div class="week-days">
                  <div
                    class="week-day-label"
                    v-for="day in [
                      'SEN',
                      'SEL',
                      'RAB',
                      'KAM',
                      'JUM',
                      'SAB',
                      'MIN',
                    ]"
                    :key="day"
                  >
                    {{ day }}
                  </div>
                </div>
                <div class="dates-grid week-mode">
                  <div
                    v-for="(dateObj, idx) in weekDates"
                    :key="idx"
                    class="date-cell"
                    :class="{ 'is-today': dateObj.isToday }"
                    @click="openDialog(dateObj.dateStr)"
                  >
                    <span class="date-num"
                      >{{ dateObj.day }}
                      {{ formatMonthShort(dateObj.date) }}</span
                    >
                    <div class="cell-content">
                      <div
                        v-for="sched in getSchedulesForDate(dateObj.dateStr)"
                        :key="sched.id"
                        class="schedule-badge"
                        :class="sched.shift_type.toLowerCase()"
                        @click.stop="editSchedule(sched)"
                      >
                        <span class="badge-text">{{
                          sched.user ? sched.user.name : "Petugas"
                        }}</span>
                        <span class="badge-time">{{
                          sched.shift_type === "Pagi"
                            ? "08-12"
                            : sched.shift_type === "Siang"
                            ? "13-16"
                            : "08-16"
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- ── LIST VIEW ── -->
              <template v-else-if="viewMode === 'list'">
                <div class="list-view pa-4">
                  <div
                    v-if="monthSchedules.length === 0"
                    class="empty-list grey--text text-center pa-8"
                  >
                    Tidak ada jadwal untuk bulan ini.
                  </div>
                  <div
                    v-for="group in groupedSchedules"
                    :key="group.date"
                    class="list-group"
                  >
                    <div class="list-date-header">
                      <span class="l-day-num">{{ group.day }}</span>
                      <span class="l-day-name">{{ group.dayName }}</span>
                    </div>
                    <div class="list-items">
                      <div
                        v-for="sched in group.items"
                        :key="sched.id"
                        class="list-item-row"
                        @click="editSchedule(sched)"
                      >
                        <v-avatar size="36" class="mr-4"
                          ><v-img :src="getPhoto(sched.user)"></v-img
                        ></v-avatar>
                        <div class="l-info">
                          <span class="l-name white--text font-weight-bold">{{
                            sched.user ? sched.user.name : "Petugas"
                          }}</span>
                          <span
                            class="l-shift-tag"
                            :class="
                              sched.shift_type.toLowerCase().replace(' ', '')
                            "
                            >{{ sched.shift_type }} Shift</span
                          >
                        </div>
                        <v-spacer></v-spacer>
                        <v-btn icon small dark
                          ><v-icon>mdi-chevron-right</v-icon></v-btn
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </v-card>
        </v-col>

        <!-- ══ RIGHT COLUMN: SIDEBAR ══ -->
        <v-col cols="12" lg="4">
          <!-- Petugas Hari Ini -->
          <v-card
            hover
            color="grey darken-4"
            class="sidebar-card mb-6 rounded-xl pa-6"
          >
            <div class="sidebar-header d-flex align-center mb-6">
              <v-icon color="cyan lighten-3" class="mr-3"
                >mdi-calendar-check</v-icon
              >
              <h3 class="white--text mb-0">Petugas Hari Ini</h3>
            </div>

            <div class="duty-list">
              <!-- Case: Full Day -->
              <template v-if="todaySchedules.isFullDay">
                <div
                  class="duty-item grey darken-3 rounded-lg pa-4 mb-4 clickable-duty"
                  style="border-left: 4px solid #00e676"
                  @click="editSchedule(todaySchedules.data)"
                >
                  <div
                    class="duty-meta d-flex justify-space-between align-center mb-3"
                  >
                    <span
                      class="shift-label green--text text--accent-3 font-weight-bold"
                      style="font-size: 0.7rem; letter-spacing: 0.1em"
                      >FULL DAY SHIFT</span
                    >
                    <span
                      class="shift-range grey--text"
                      style="font-size: 0.7rem"
                      >{{ getShiftTime("Full Day") }}</span
                    >
                  </div>
                  <div class="duty-person d-flex align-center">
                    <v-avatar size="48" class="person-avatar grey darken-4">
                      <v-img
                        :src="getPhoto(todaySchedules.data.user)"
                        aspect-ratio="1"
                        cover
                        position="center top"
                      ></v-img>
                    </v-avatar>
                    <div class="person-info ml-3 flex-grow-1">
                      <p class="person-name white--text mb-0 font-weight-bold">
                        {{
                          todaySchedules.data.user
                            ? todaySchedules.data.user.name
                            : "Petugas"
                        }}
                      </p>
                      <p class="person-role grey--text text-caption mb-0">
                        Petugas Pelayanan
                      </p>
                    </div>
                    <v-icon color="green accent-4">mdi-check-decagram</v-icon>
                  </div>
                </div>
              </template>

              <!-- Case: Split Shifts -->
              <template v-else>
                <!-- Pagi -->
                <div
                  class="duty-item grey darken-3 rounded-lg pa-4 mb-4 clickable-duty"
                  @click="
                    todaySchedules.pagi
                      ? editSchedule(todaySchedules.pagi)
                      : openDialog(formatDateStr(new Date()), 'Pagi')
                  "
                >
                  <div
                    class="duty-meta d-flex justify-space-between align-center mb-3"
                  >
                    <span
                      class="shift-label cyan--text text--lighten-3 font-weight-bold"
                      style="font-size: 0.7rem; letter-spacing: 0.1em"
                      >SHIFT PAGI</span
                    >
                    <span
                      class="shift-range grey--text"
                      style="font-size: 0.7rem"
                      >{{ getShiftTime("Pagi") }}</span
                    >
                  </div>
                  <div
                    v-if="todaySchedules.pagi"
                    class="duty-person d-flex align-center"
                  >
                    <v-avatar size="48" class="person-avatar grey darken-3">
                      <v-img
                        :src="getPhoto(todaySchedules.pagi.user)"
                        aspect-ratio="1"
                        cover
                        position="center top"
                      ></v-img>
                    </v-avatar>
                    <div class="person-info ml-3 flex-grow-1">
                      <p class="person-name white--text mb-0 font-weight-bold">
                        {{
                          todaySchedules.pagi.user
                            ? todaySchedules.pagi.user.name
                            : "Petugas"
                        }}
                      </p>
                      <p class="person-role grey--text text-caption mb-0">
                        Petugas Pelayanan
                      </p>
                    </div>
                    <v-icon small color="green accent-4"
                      >mdi-check-circle</v-icon
                    >
                  </div>
                  <div
                    v-else
                    class="duty-empty grey--text text-italic text-center pa-2"
                    style="font-size: 0.85rem"
                  >
                    Belum ada petugas
                  </div>
                </div>

                <!-- Siang -->
                <div
                  class="duty-item grey darken-3 rounded-lg pa-4 clickable-duty"
                  @click="
                    todaySchedules.siang
                      ? editSchedule(todaySchedules.siang)
                      : openDialog(formatDateStr(new Date()), 'Siang')
                  "
                >
                  <div
                    class="duty-meta d-flex justify-space-between align-center mb-3"
                  >
                    <span
                      class="shift-label orange--text text--lighten-2 font-weight-bold"
                      style="font-size: 0.7rem; letter-spacing: 0.1em"
                      >SHIFT SIANG</span
                    >
                    <span
                      class="shift-range grey--text"
                      style="font-size: 0.7rem"
                      >{{ getShiftTime("Siang") }}</span
                    >
                  </div>
                  <div
                    v-if="todaySchedules.siang"
                    class="duty-person d-flex align-center"
                  >
                    <v-avatar size="48" class="person-avatar grey darken-3">
                      <v-img
                        :src="getPhoto(todaySchedules.siang.user)"
                        aspect-ratio="1"
                        cover
                        position="center top"
                      ></v-img>
                    </v-avatar>
                    <div class="person-info ml-3 flex-grow-1">
                      <p class="person-name white--text mb-0 font-weight-bold">
                        {{
                          todaySchedules.siang.user
                            ? todaySchedules.siang.user.name
                            : "Petugas"
                        }}
                      </p>
                      <p class="person-role grey--text text-caption mb-0">
                        Petugas Pelayanan
                      </p>
                    </div>
                    <v-icon small color="orange accent-2"
                      >mdi-minus-circle</v-icon
                    >
                  </div>
                  <div
                    v-else
                    class="duty-empty grey--text text-italic text-center pa-2"
                    style="font-size: 0.85rem"
                  >
                    Belum ada petugas
                  </div>
                </div>
              </template>
            </div>
          </v-card>

          <!-- Hari Libur -->
          <v-card
            hover
            color="grey darken-4"
            class="sidebar-card rounded-xl pa-6"
          >
            <div class="sidebar-header d-flex align-center mb-6">
              <v-icon color="red lighten-2" class="mr-3"
                >mdi-party-popper</v-icon
              >
              <h3 class="white--text mb-0">Hari Libur</h3>
            </div>
            <div class="holiday-list">
              <div
                v-if="holidays.length === 0"
                class="grey--text text-center pa-4 holiday-empty-state"
                style="
                  font-size: 0.85rem;
                  cursor: pointer;
                  border: 1px dashed #757575;
                  border-radius: 8px;
                "
                @click="openAddHoliday"
              >
                Tidak ada hari libur tercatat.<br />
                <span class="cyan--text text--lighten-3"
                  >Klik untuk tambah</span
                >
              </div>
              <div
                v-for="holiday in displayHolidays"
                :key="holiday.id"
                class="holiday-item d-flex align-center mb-5"
                style="cursor: pointer"
                @click="editHoliday(holiday)"
              >
                <div
                  class="holiday-date-box grey darken-3 rounded-lg d-flex flex-column align-center justify-center mr-4"
                  style="width: 52px; height: 52px"
                >
                  <span
                    class="h-month red--text text--lighten-4 font-weight-bold"
                    style="font-size: 0.65rem"
                    >{{ holiday.month }}</span
                  >
                  <span
                    class="h-day red--text text--lighten-2 font-weight-black"
                    style="font-size: 1.1rem"
                    >{{ holiday.day }}</span
                  >
                </div>
                <div class="holiday-info flex-grow-1">
                  <p
                    class="h-title white--text mb-0 font-weight-bold"
                    style="font-size: 0.95rem"
                  >
                    {{ holiday.name }}
                  </p>
                  <p class="h-sub grey--text mb-0 text-caption">
                    {{ holiday.dateFormatted }}
                  </p>
                </div>
                <v-btn icon x-small @click.stop="deleteHoliday(holiday.id)">
                  <v-icon small color="red lighten-2">mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Dialog: Add/Edit Schedule -->
    <v-dialog v-model="dialog" max-width="450px" persistent>
      <v-card
        class="edit-dialog-card rounded-xl overflow-hidden"
        color="grey darken-4"
        dark
      >
        <v-card-title class="headline pa-6 white--text">
          <v-icon class="mr-3" color="cyan lighten-3">mdi-clock-edit</v-icon>
          {{ isEdit ? "Ubah" : "Atur" }} Jadwal
        </v-card-title>
        <v-card-text class="pa-6 pt-0">
          <div class="dialog-form">
            <div class="form-group mb-6">
              <label class="text-overline grey--text text--lighten-1"
                >Tanggal</label
              >
              <div
                class="form-value-box grey darken-3 pa-4 rounded-lg white--text font-weight-bold"
              >
                {{ formatDateFull(form.date) }}
              </div>
            </div>

            <v-autocomplete
              v-model="form.user_id"
              :items="petugasList"
              item-text="name"
              item-value="id"
              label="Pilih Petugas"
              outlined
              dark
              class="mt-4"
              color="cyan lighten-3"
              prepend-inner-icon="mdi-account"
            ></v-autocomplete>

            <v-select
              v-model="form.shift_type"
              :items="shiftOptions"
              label="Pilih Shift"
              outlined
              dark
              color="cyan lighten-3"
              prepend-inner-icon="mdi-clock-outline"
              :disabled="shiftOptions.length === 0"
              :hint="
                shiftOptions.length === 0
                  ? 'Hari ini sudah penuh (Pagi & Siang terisi)'
                  : ''
              "
              persistent-hint
            ></v-select>
          </div>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-btn
            v-if="isEdit"
            color="red lighten-1"
            text
            @click="confirmDelete"
            :loading="deleting"
            >Hapus</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="grey lighten-1" text @click="closeDialog">Batal</v-btn>
          <v-btn
            color="cyan darken-1"
            depressed
            dark
            @click="save"
            :loading="saving"
            >Simpan</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Import Schedule -->
    <v-dialog v-model="importDialog" max-width="550px">
      <v-card color="grey darken-4" dark class="rounded-xl">
        <v-card-title class="headline pa-6">
          <v-icon
            class="mr-3"
            :color="importTab === 'jadwal' ? 'cyan lighten-3' : 'red lighten-2'"
            >{{
              importTab === "jadwal" ? "mdi-file-import" : "mdi-calendar-remove"
            }}</v-icon
          >
          {{
            importTab === "jadwal"
              ? "Import Jadwal Petugas"
              : "Import Hari Libur"
          }}
        </v-card-title>
        <v-card-text class="pa-6 pt-0">
          <v-file-input
            v-model="importFile"
            :label="
              importTab === 'jadwal'
                ? 'Pilih File Excel Jadwal'
                : 'Pilih File Excel Libur'
            "
            outlined
            color="cyan lighten-3"
            prepend-icon="mdi-file-excel"
            accept=".xlsx,.xls,.csv"
            @change="onImportFileChange"
          ></v-file-input>
          <p class="text-caption grey--text mb-2">
            Pastikan format file sesuai dengan template.
          </p>
          <v-btn small text color="cyan lighten-3" @click="downloadTemplate">
            <v-icon left small>mdi-download</v-icon> Download Template
            {{ importTab === "jadwal" ? "Jadwal" : "Libur" }}
          </v-btn>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="importDialog = false">Batal</v-btn>
          <v-btn
            color="cyan darken-1"
            depressed
            :loading="importLoading"
            :disabled="!importFile"
            @click="doImport"
            >Unggah</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Import Results -->
    <v-dialog v-model="importResultDialog" max-width="700px">
      <v-card color="grey darken-4" dark class="rounded-xl">
        <v-card-title class="headline pa-6">
          <v-icon class="mr-3" color="green accent-3">mdi-check-circle</v-icon>
          Hasil Import
        </v-card-title>
        <v-card-text class="pa-6 pt-0">
          <div class="d-flex mb-4" style="gap: 16px">
            <v-chip color="green darken-1" dark small
              ><v-icon left small>mdi-check</v-icon> Sukses:
              {{ importResult.success }}</v-chip
            >
            <v-chip color="orange darken-1" dark small
              ><v-icon left small>mdi-alert</v-icon> Dilewati:
              {{ importResult.skipped }}</v-chip
            >
            <v-chip color="red darken-1" dark small
              ><v-icon left small>mdi-close</v-icon> Error:
              {{ (importResult.errors || []).length }}</v-chip
            >
          </div>
          <v-simple-table
            v-if="importResult.errors && importResult.errors.length > 0"
            dark
            dense
            class="rounded-lg"
          >
            <thead>
              <tr>
                <th>Baris</th>
                <th>Email/Nama</th>
                <th>Detail</th>
                <th>Alasan</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(err, i) in importResult.errors" :key="i">
                <td>{{ err.row }}</td>
                <td>{{ err.email || err.name || "-" }}</td>
                <td>
                  {{ err.shift ? err.shift + " / " + err.date : err.date }}
                </td>
                <td class="red--text text--lighten-2">{{ err.reason }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <p v-else class="green--text text--accent-3 text-center pa-4">
            Semua data berhasil diimport!
          </p>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            color="cyan darken-1"
            depressed
            @click="importResultDialog = false"
            >Tutup</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Add Holiday -->
    <v-dialog v-model="holidayDialog" max-width="450px">
      <v-card color="grey darken-4" dark class="rounded-xl">
        <v-card-title class="headline pa-6">
          <v-icon class="mr-3" color="red lighten-2">mdi-party-popper</v-icon>
          {{ holidayForm.id ? "Ubah Hari Libur" : "Tambah Hari Libur" }}
        </v-card-title>
        <v-card-text class="pa-6 pt-0">
          <v-text-field
            v-model="holidayForm.name"
            label="Nama Libur"
            outlined
            color="cyan lighten-3"
            prepend-inner-icon="mdi-tag"
          ></v-text-field>
          <v-menu
            v-model="holidayDateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="holidayForm.date"
                label="Tanggal"
                outlined
                color="cyan lighten-3"
                readonly
                prepend-inner-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="holidayForm.date"
              @input="holidayDateMenu = false"
              color="cyan darken-1"
            ></v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-btn
            v-if="holidayForm.id"
            text
            color="red lighten-1"
            @click="deleteHoliday(holidayForm.id)"
            >Hapus</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn text @click="holidayDialog = false">Batal</v-btn>
          <v-btn
            color="red lighten-1"
            depressed
            :loading="holidaySaving"
            :disabled="!holidayForm.name || !holidayForm.date"
            @click="saveHoliday"
            >Simpan</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

export default {
  name: "JadwalPST",
  data() {
    return {
      currentDate: new Date(),
      viewMode: "month",
      weekDays: [
        "SENIN",
        "SELASA",
        "RABU",
        "KAMIS",
        "JUMAT",
        "SABTU",
        "MINGGU",
      ],
      schedules: [],
      petugasList: [],
      loading: false,
      saving: false,
      deleting: false,
      dialog: false,
      importDialog: false,
      holidayDialog: false,
      isEdit: false,
      form: {
        id: null,
        date: "",
        user_id: null,
        shift_type: "",
      },
      // Import
      importFile: null,
      importTab: "jadwal",
      importLoading: false,
      importResult: { success: 0, skipped: 0, errors: [] },
      importResultDialog: false,
      // Holidays
      holidays: [],
      holidayForm: { id: null, name: "", date: "" },
      holidayDateMenu: false,
      holidaySaving: false,
    };
  },
  computed: {
    calendarDates() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0=Sun, 1=Mon
      const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const dates = [];

      // Previous month days
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = startOffset - 1; i >= 0; i--) {
        const d = new Date(year, month - 1, prevMonthLastDay - i);
        dates.push({
          day: d.getDate(),
          dateStr: this.formatDateStr(d),
          date: d,
          isCurrentMonth: false,
          isToday: this.checkToday(d),
        });
      }

      // Current month days
      for (let i = 1; i <= daysInMonth; i++) {
        const d = new Date(year, month, i);
        dates.push({
          day: i,
          dateStr: this.formatDateStr(d),
          date: d,
          isCurrentMonth: true,
          isToday: this.checkToday(d),
        });
      }

      // Next month days
      const remaining = 42 - dates.length;
      for (let i = 1; i <= remaining; i++) {
        const d = new Date(year, month + 1, i);
        dates.push({
          day: i,
          dateStr: this.formatDateStr(d),
          date: d,
          isCurrentMonth: false,
          isToday: this.checkToday(d),
        });
      }

      return dates;
    },
    weekDates() {
      const curr = new Date(this.currentDate);
      const day = curr.getDay(); // 0=Sun
      const diff = curr.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
      curr.setDate(diff);

      const dates = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(curr);
        dates.push({
          day: d.getDate(),
          dateStr: this.formatDateStr(d),
          date: d,
          isToday: this.checkToday(d),
        });
        curr.setDate(curr.getDate() + 1);
      }
      return dates;
    },
    processedSchedules() {
      const dateMap = {};
      this.schedules.forEach((s) => {
        const uId = String(s.user_id);
        if (!dateMap[s.date]) dateMap[s.date] = {};
        if (!dateMap[s.date][uId]) {
          dateMap[s.date][uId] = {
            user: s.user,
            shifts: [],
            ids: [],
          };
        }
        dateMap[s.date][uId].shifts.push(s.shift_type);
        dateMap[s.date][uId].ids.push(s.id);
      });

      const processed = [];
      Object.keys(dateMap).forEach((date) => {
        Object.keys(dateMap[date]).forEach((userId) => {
          const entry = dateMap[date][userId];
          const normalizedShifts = entry.shifts.map((s) => s.toLowerCase());
          let type = entry.shifts[0];

          if (
            normalizedShifts.includes("pagi") &&
            normalizedShifts.includes("siang")
          ) {
            type = "Full Day";
          }
          processed.push({
            id: entry.ids[0],
            date: date,
            user_id: parseInt(userId),
            user: entry.user,
            shift_type: type,
            original_shifts: entry.shifts,
          });
        });
      });
      return processed;
    },
    monthSchedules() {
      const month = this.currentDate.getMonth();
      const year = this.currentDate.getFullYear();
      return this.processedSchedules
        .filter((s) => {
          const d = this.parseLocalDate(s.date);
          return d.getMonth() === month && d.getFullYear() === year;
        })
        .sort((a, b) => a.date.localeCompare(b.date));
    },
    groupedSchedules() {
      const groups = {};
      this.monthSchedules.forEach((s) => {
        if (!groups[s.date]) {
          const d = this.parseLocalDate(s.date);
          groups[s.date] = {
            date: s.date,
            day: d.getDate(),
            dayName: this.weekDays[d.getDay() === 0 ? 6 : d.getDay() - 1], // Correct index for Monday-start
            items: [],
          };
        }
        groups[s.date].items.push(s);
      });
      return Object.values(groups);
    },
    todaySchedules() {
      const today = this.formatDateStr(new Date());
      const todayScheds = this.processedSchedules.filter(
        (s) => s.date === today
      );

      const fullDay = todayScheds.find((s) => s.shift_type === "Full Day");
      if (fullDay) {
        return { isFullDay: true, data: fullDay };
      }
      return {
        isFullDay: false,
        pagi: todayScheds.find((s) => s.shift_type === "Pagi"),
        siang: todayScheds.find((s) => s.shift_type === "Siang"),
      };
    },
    shiftOptions() {
      if (!this.form.date) return ["Pagi", "Siang", "Full Day"];

      const dayScheds = this.schedules.filter((s) => s.date === this.form.date);

      // If we are in "Atur Jadwal" (New)
      if (!this.isEdit) {
        if (dayScheds.length >= 2) return []; // Already full (Pagi & Siang)
        if (dayScheds.length === 0) return ["Pagi", "Siang", "Full Day"];

        // One shift is already taken, can only take the other one
        const taken = dayScheds[0].shift_type;
        return taken === "Pagi" ? ["Siang"] : ["Pagi"];
      }

      // If we are in "Ubah Jadwal" (Edit)
      if (this.form.shift_type === "Full Day") {
        // Can split Full Day back into Pagi or Siang
        return ["Pagi", "Siang", "Full Day"];
      }

      // If it's a partial shift, check if we can expand to Full Day
      const otherShiftType = this.form.shift_type === "Pagi" ? "Siang" : "Pagi";
      const otherShift = dayScheds.find((s) => s.shift_type === otherShiftType);

      const opts = [this.form.shift_type]; // Current choice

      if (!otherShift) {
        opts.push(otherShiftType); // Can switch to other
        opts.push("Full Day"); // Can expand to full
      } else if (otherShift.user_id === this.form.user_id) {
        // Other shift belongs to same person, allow "Full Day" label
        opts.push("Full Day");
      }

      return opts;
    },
    displayHolidays() {
      return this.holidays.map((h) => {
        const d = this.parseLocalDate(h.date);
        return {
          ...h,
          month: d
            .toLocaleDateString("id-ID", { month: "short" })
            .toUpperCase(),
          day: String(d.getDate()).padStart(2, "0"),
          dateFormatted: d.toLocaleDateString("id-ID", {
            day: "numeric",
            month: "long",
            year: "numeric",
          }),
        };
      });
    },
  },
  mounted() {
    this.fetchPetugas();
    this.fetchSchedules();
    this.fetchHolidays();
  },
  methods: {
    parseLocalDate(dateStr) {
      if (!dateStr) return null;
      const parts = dateStr.substring(0, 10).split("-");
      if (parts.length !== 3) return new Date(dateStr);
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return new Date(year, month, day);
    },
    fetchPetugas() {
      axios
        .get("getAllPetugasLayanan", {
          headers: {
            Authorization:
              "Bearer " + this.$store.getters["userAuth/activeToken"],
          },
        })
        .then((res) => {
          this.petugasList = res.data;
        })
        .catch((err) => console.error(err));
    },
    fetchSchedules() {
      this.loading = true;
      const month = this.currentDate.getMonth() + 1;
      const year = this.currentDate.getFullYear();

      axios
        .get(`schedules?month=${month}&year=${year}`, {
          headers: {
            Authorization:
              "Bearer " + this.$store.getters["userAuth/activeToken"],
          },
        })
        .then((res) => {
          this.schedules = res.data;
        })
        .catch((err) => console.error(err))
        .finally(() => {
          this.loading = false;
        });
    },
    getSchedulesForDate(dateStr) {
      const order = { Pagi: 1, Siang: 2, "Full Day": 0 }; // Full Day first or as needed
      return this.processedSchedules
        .filter((s) => s.date === dateStr)
        .sort(
          (a, b) => (order[a.shift_type] || 9) - (order[b.shift_type] || 9)
        );
    },
    isHoliday(dateStr) {
      // Check weekends (Sabtu/Minggu)
      const d = this.parseLocalDate(dateStr);
      if (d.getDay() === 0 || d.getDay() === 6) return true;
      // Check API holidays
      return this.holidays.some((h) => {
        const hDate = h.date.substring(0, 10);
        return hDate === dateStr;
      });
    },
    nextMonth() {
      if (this.viewMode === "week") {
        this.currentDate = new Date(
          this.currentDate.getTime() + 7 * 24 * 60 * 60 * 1000
        );
      } else {
        this.currentDate = new Date(
          this.currentDate.getFullYear(),
          this.currentDate.getMonth() + 1,
          1
        );
      }
      this.fetchSchedules();
      this.fetchHolidays();
    },
    prevMonth() {
      if (this.viewMode === "week") {
        this.currentDate = new Date(
          this.currentDate.getTime() - 7 * 24 * 60 * 60 * 1000
        );
      } else {
        this.currentDate = new Date(
          this.currentDate.getFullYear(),
          this.currentDate.getMonth() - 1,
          1
        );
      }
      this.fetchSchedules();
      this.fetchHolidays();
    },
    setToday() {
      this.currentDate = new Date();
      this.fetchSchedules();
      this.fetchHolidays();
    },
    openDialog(dateStr, shiftType = "Pagi") {
      if (this.isHoliday(dateStr)) {
        const d = this.parseLocalDate(dateStr);
        // Jika Sabtu/Minggu, tampilkan alert saja
        if (d.getDay() === 0 || d.getDay() === 6) {
          Swal.fire(
            "Info",
            "Sabtu dan Minggu adalah hari libur otomatis.",
            "info"
          );
          return;
        }

        // Jika hari libur kustom, buka dialog edit libur
        const holiday = this.holidays.find(
          (h) => h.date.substring(0, 10) === dateStr
        );
        if (holiday) {
          this.holidayForm = {
            id: holiday.id,
            name: holiday.name,
            date: dateStr,
          };
          this.holidayDialog = true;
        }
        return;
      }
      this.isEdit = false;
      this.form = {
        id: null,
        date: dateStr,
        user_id: null,
        shift_type: shiftType,
      };
      this.dialog = true;
    },
    editSchedule(sched) {
      this.isEdit = true;
      this.form = {
        id: sched.id,
        date: sched.date,
        user_id: sched.user_id,
        shift_type: sched.shift_type,
        original: JSON.parse(JSON.stringify(sched)), // Store original state
      };
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
    },
    save() {
      if (!this.form.user_id || !this.form.shift_type) {
        return Swal.fire("Error", "Lengkapi semua form", "error");
      }

      // Check if day is already full or shift is taken (only for new entries)
      if (!this.isEdit) {
        const dayScheds = this.schedules.filter(
          (s) => s.date === this.form.date
        );
        const isFull = dayScheds.length >= 2;

        if (isFull) {
          return Swal.fire(
            "Gagal",
            "Jadwal hari ini sudah penuh (Pagi & Siang terisi).",
            "error"
          );
        }

        if (this.form.shift_type === "Full Day" && dayScheds.length > 0) {
          return Swal.fire(
            "Gagal",
            "Sudah ada jadwal parsial. Hapus dulu jika ingin set Full Day.",
            "error"
          );
        }

        if (
          this.form.shift_type !== "Full Day" &&
          dayScheds.some((s) => s.shift_type === this.form.shift_type)
        ) {
          return Swal.fire(
            "Gagal",
            `Shift ${this.form.shift_type} sudah terisi.`,
            "error"
          );
        }
      }

      this.saving = true;

      const saveExecution = () => {
        // If Full Day, we send two separate requests
        const shiftsToSave =
          this.form.shift_type === "Full Day"
            ? ["Pagi", "Siang"]
            : [this.form.shift_type];

        const promises = shiftsToSave.map((shift) => {
          return axios.post(
            "schedules",
            {
              date: this.form.date,
              user_id: this.form.user_id,
              shift_type: shift,
            },
            {
              headers: {
                Authorization:
                  "Bearer " + this.$store.getters["userAuth/activeToken"],
              },
            }
          );
        });

        Promise.all(promises)
          .then(() => {
            this.fetchSchedules();
            this.closeDialog();
            Swal.fire("Sukses", "Jadwal tersimpan", "success");
          })
          .catch((err) => {
            console.error(err);
            Swal.fire("Error", "Gagal menyimpan jadwal", "error");
          })
          .finally(() => {
            this.saving = false;
          });
      };

      // If editing, delete original records first to ensure replacement
      if (this.isEdit && this.form.original) {
        const originalShifts = this.form.original.original_shifts || [
          this.form.original.shift_type,
        ];
        const deletePromises = originalShifts.map((shift) => {
          return axios.post(
            "schedules/delete",
            {
              user_id: this.form.original.user_id,
              date: this.form.original.date,
              shift_type: shift,
            },
            {
              headers: {
                Authorization:
                  "Bearer " + this.$store.getters["userAuth/activeToken"],
              },
            }
          );
        });

        Promise.all(deletePromises)
          .then(() => saveExecution())
          .catch((err) => {
            console.error("Delete failed during edit", err);
            saveExecution(); // Try to save anyway
          });
      } else {
        saveExecution();
      }
    },
    confirmDelete() {
      const text =
        this.form.shift_type === "Full Day"
          ? "Seluruh jadwal Full Day (Pagi & Siang) akan dihapus."
          : `Jadwal ${this.form.shift_type} akan dihapus.`;

      Swal.fire({
        title: "Hapus jadwal?",
        text: text,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ff5252",
        confirmButtonText: "Ya, Hapus",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteSchedule();
        }
      });
    },
    deleteSchedule() {
      this.deleting = true;
      // If Full Day, delete both morning and afternoon entries
      const shiftsToDelete =
        this.form.shift_type === "Full Day"
          ? ["Pagi", "Siang"]
          : [this.form.shift_type];

      const promises = shiftsToDelete.map((shift) => {
        return axios.post(
          "schedules/delete",
          {
            user_id: this.form.user_id,
            date: this.form.date,
            shift_type: shift,
          },
          {
            headers: {
              Authorization:
                "Bearer " + this.$store.getters["userAuth/activeToken"],
            },
          }
        );
      });

      Promise.all(promises)
        .then(() => {
          this.fetchSchedules();
          this.closeDialog();
          Swal.fire("Terhapus", "Jadwal dihapus", "success");
        })
        .catch((err) => {
          console.error(err);
          Swal.fire("Error", "Gagal menghapus jadwal", "error");
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    // Helper Formatters
    formatMonthYear(date) {
      return date.toLocaleDateString("id-ID", {
        month: "long",
        year: "numeric",
      });
    },
    formatMonthShort(date) {
      return date.toLocaleDateString("id-ID", { month: "short" });
    },
    formatDateStr(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    formatDateFull(dateStr) {
      if (!dateStr) return "";
      return this.parseLocalDate(dateStr).toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },
    formatShortName(name) {
      if (!name) return "";
      return name.split(" ")[0];
    },
    checkToday(date) {
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    },
    getShiftTime(shiftType) {
      const isFriday = new Date().getDay() === 5;
      const endTime = isFriday ? "16:30" : "16:00";
      if (shiftType === "Pagi") return "08:00 - 12:00";
      if (shiftType === "Siang") return `13:00 - ${endTime}`;
      if (shiftType === "Full Day") return `08:00 - ${endTime}`;
      return "";
    },
    getPhoto(user) {
      if (!user) {
        return "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";
      }
      if (user.fotoPelayan) {
        return (
          axios.defaults.baseURL.replace(/\/api\/?$/, "/") + user.fotoPelayan
        );
      }
      return (
        user.img_url ||
        "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"
      );
    },
    // ═══ HOLIDAYS ═══
    fetchHolidays() {
      const month = this.currentDate.getMonth() + 1;
      const year = this.currentDate.getFullYear();
      axios
        .get(`holidays?month=${month}&year=${year}`)
        .then((res) => {
          this.holidays = res.data;
        })
        .catch((err) => console.error(err));
    },
    openAddHoliday() {
      this.holidayForm = { id: null, name: "", date: "" };
      this.holidayDialog = true;
    },
    editHoliday(holiday) {
      this.holidayForm = {
        id: holiday.id,
        name: holiday.name,
        date: holiday.date.substring(0, 10),
      };
      this.holidayDialog = true;
    },
    saveHoliday() {
      if (!this.holidayForm.name || !this.holidayForm.date) return;
      this.holidaySaving = true;
      axios
        .post("holidays", this.holidayForm, {
          headers: {
            Authorization:
              "Bearer " + this.$store.getters["userAuth/activeToken"],
          },
        })
        .then(() => {
          this.fetchHolidays();
          this.holidayDialog = false;
          this.holidayForm = { name: "", date: "" };
          Swal.fire("Sukses", "Hari libur ditambahkan", "success");
        })
        .catch((err) => {
          console.error(err);
          const msg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Gagal menyimpan hari libur";
          Swal.fire("Error", msg, "error");
        })
        .finally(() => {
          this.holidaySaving = false;
        });
    },
    deleteHoliday(id) {
      Swal.fire({
        title: "Hapus hari libur?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ff5252",
        confirmButtonText: "Ya, Hapus",
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .delete(`holidays/${id}`, {
              headers: {
                Authorization:
                  "Bearer " + this.$store.getters["userAuth/activeToken"],
              },
            })
            .then(() => {
              this.fetchHolidays();
              this.holidayDialog = false;
              Swal.fire("Terhapus", "Hari libur dihapus", "success");
            })
            .catch((err) => {
              console.error(err);
              Swal.fire("Error", "Gagal menghapus", "error");
            });
        }
      });
    },
    // ═══ IMPORT ═══
    onImportFileChange() {
      // Reset when file changes
    },
    openImport(tab) {
      this.importTab = tab;
      this.importFile = null;
      this.importDialog = true;
    },
    doImport() {
      if (!this.importFile) return;
      this.importLoading = true;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          if (this.importTab === "jadwal") {
            this.processScheduleImport(workbook);
          } else {
            this.processHolidayImport(workbook);
          }
        } catch (err) {
          console.error(err);
          Swal.fire(
            "Error",
            "Gagal membaca file Excel: " + err.message,
            "error"
          );
          this.importLoading = false;
        }
      };
      reader.readAsArrayBuffer(this.importFile);
    },
    processScheduleImport(workbook) {
      const sheetName =
        workbook.SheetNames.find((n) => n.toLowerCase() === "data") ||
        workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet);

      const schedules = rows
        .map((row) => {
          // Handle Excel date serial numbers
          let dateVal =
            row.Tanggal ||
            row.tanggal ||
            row.TANGGAL ||
            row.Date ||
            row.date ||
            "";
          if (typeof dateVal === "number") {
            const d = XLSX.SSF.parse_date_code(dateVal);
            dateVal = `${d.y}-${String(d.m).padStart(2, "0")}-${String(
              d.d
            ).padStart(2, "0")}`;
          }
          return {
            email: row["Email Petugas"] || row.email || row.Email || "",
            shift: row.Shift || row.shift || row.SHIFT || "",
            date: String(dateVal),
          };
        })
        .filter((r) => r.email && r.date);

      if (schedules.length === 0) {
        Swal.fire(
          "Error",
          'Tidak ada data valid di sheet "data". Pastikan kolom: Email Petugas, Shift, Tanggal',
          "error"
        );
        this.importLoading = false;
        return;
      }

      axios
        .post(
          "schedules/import",
          { schedules },
          {
            headers: {
              Authorization:
                "Bearer " + this.$store.getters["userAuth/activeToken"],
            },
          }
        )
        .then((res) => {
          this.importResult = res.data;
          this.importDialog = false;
          this.importFile = null;
          this.importResultDialog = true;
          this.fetchSchedules();
        })
        .catch((err) => {
          console.error(err);
          Swal.fire("Error", "Gagal import jadwal", "error");
        })
        .finally(() => {
          this.importLoading = false;
        });
    },
    processHolidayImport(workbook) {
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      const holidays = rows
        .map((row) => {
          let dateVal =
            row.Tanggal ||
            row.tanggal ||
            row.TANGGAL ||
            row.Date ||
            row.date ||
            "";
          if (typeof dateVal === "number") {
            const d = XLSX.SSF.parse_date_code(dateVal);
            dateVal = `${d.y}-${String(d.m).padStart(2, "0")}-${String(
              d.d
            ).padStart(2, "0")}`;
          }
          return {
            name: row["Nama Libur"] || row.nama || row.Nama || "",
            date: String(dateVal),
          };
        })
        .filter((r) => r.name && r.date);

      if (holidays.length === 0) {
        Swal.fire(
          "Error",
          "Tidak ada data valid. Pastikan kolom: Nama Libur, Tanggal",
          "error"
        );
        this.importLoading = false;
        return;
      }

      axios
        .post(
          "holidays/import",
          { holidays },
          {
            headers: {
              Authorization:
                "Bearer " + this.$store.getters["userAuth/activeToken"],
            },
          }
        )
        .then((res) => {
          this.importResult = res.data;
          this.importDialog = false;
          this.importFile = null;
          this.importResultDialog = true;
          this.fetchHolidays();
        })
        .catch((err) => {
          console.error(err);
          Swal.fire("Error", "Gagal import hari libur", "error");
        })
        .finally(() => {
          this.importLoading = false;
        });
    },
    downloadTemplate() {
      if (this.importTab === "jadwal") {
        this.downloadScheduleTemplate();
      } else {
        this.downloadHolidayTemplate();
      }
    },
    downloadScheduleTemplate() {
      // Sheet 1: data
      const dataSheet = XLSX.utils.aoa_to_sheet([
        ["No", "Email Petugas", "Shift", "Tanggal"],
        [1, "contoh@bps.go.id", "Pagi", "2026-05-05"],
      ]);
      // Sheet 2: master (from loaded list + Keterangan Shift)
      const masterRows = [
        ["Keterangan Shift:"],
        ["Pagi", "Siang", "Full Day"],
        [],
        ["No", "Nama", "Email"],
      ];
      this.petugasList.forEach((p, i) => {
        masterRows.push([i + 1, p.name, p.email]);
      });
      const masterSheet = XLSX.utils.aoa_to_sheet(masterRows);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, dataSheet, "data");
      XLSX.utils.book_append_sheet(wb, masterSheet, "master");
      XLSX.writeFile(wb, "Template_Jadwal_Petugas.xlsx");
    },
    downloadHolidayTemplate() {
      const sheet = XLSX.utils.aoa_to_sheet([
        ["No", "Nama Libur", "Tanggal"],
        [1, "Hari Kemerdekaan", "2026-08-17"],
      ]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet, "libur");
      XLSX.writeFile(wb, "Template_Hari_Libur.xlsx");
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap");

.jadwal-page {
  /* Using standard Vuetify background */
  min-height: 100vh;
  font-family: "Inter", sans-serif;
}

/* ── CALENDAR GRID ── */
.week-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 12px;
}

.week-day-label {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.3);
  letter-spacing: 0.15em;
}

.dates-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  overflow: hidden;
}

.date-cell {
  background: #2c2c2c; /* Slightly lighter than grey darken-4 for grid contrast */
  min-height: 100px;
  padding: 8px;
  position: relative;
  transition: all 0.2s ease;
  cursor: pointer;
}

.date-cell:hover {
  background: #3d3d3d;
}

.date-cell.other-month {
  opacity: 0.2;
}

.date-cell.is-today {
  background: #333333;
}
.date-cell.is-today::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #00bcd4; /* Cyan accent */
}

.date-num {
  font-size: 0.9rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
  display: block;
  margin-bottom: 6px;
}

.cell-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* ── BADGES ── */
.schedule-badge {
  font-size: 0.6rem;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.schedule-badge.pagi {
  background: rgba(0, 188, 212, 0.1);
  color: #00bcd4;
  border-left: 3px solid #00bcd4;
}

.schedule-badge.siang {
  background: rgba(255, 152, 0, 0.1);
  color: #ff9800;
  border-left: 3px solid #ff9800;
}

.badge-dot {
  margin-left: 6px;
  font-size: 8px !important;
}

.no-shift {
  font-size: 0.6rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.1);
  text-align: center;
  padding: 8px 0;
}

.holiday-badge {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  font-size: 0.6rem;
  font-weight: 800;
  padding: 4px;
  border-radius: 4px;
  text-align: center;
}

/* ── WEEK MODE ── */
.week-mode .date-cell {
  min-height: 400px;
}
.badge-time {
  font-size: 0.6rem;
  opacity: 0.6;
}

/* ── LIST VIEW ── */
.list-group {
  margin-bottom: 24px;
}
.list-date-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding-bottom: 8px;
}
.l-day-num {
  font-size: 1.5rem;
  font-weight: 800;
  color: #ffffff;
}
.l-day-name {
  font-size: 0.8rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
}
.list-item-row {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  padding: 12px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.1s;
  margin-bottom: 8px;
}
.list-item-row:hover {
  transform: translateX(4px);
  background: rgba(255, 255, 255, 0.06);
}
.l-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: #ffffff;
}
.l-shift-tag {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
}
.clickable-duty {
  cursor: pointer;
  transition: all 0.2s;
}
.clickable-duty:hover {
  background: #3d3d3d !important;
  transform: scale(1.02);
}

.l-shift-tag.pagi {
  color: #00bcd4;
}
.l-shift-tag.siang {
  color: #ff9800;
}
.l-shift-tag.full {
  color: #00e676;
}

.schedule-badge.full {
  background: rgba(0, 230, 118, 0.1);
  color: #00e676;
  border-left: 3px solid #00e676;
}

/* ── MODE TOGGLE ── */
.mode-toggle {
  background: rgba(255, 255, 255, 0.05) !important;
  border-radius: 8px !important;
  padding: 2px;
}

.month-title {
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
